/**
 * EventBus - Typed event system for decoupled communication
 *
 * Part of Phase 2 architectural redesign to reduce coupling in game.ts
 *
 * Usage:
 *   const bus = new EventBus();
 *   const unsub = bus.on('weapon:fired', (data) => console.log(data.weaponId));
 *   bus.emit('weapon:fired', { weaponId: 'bazooka', wormId: 'w1', angle: 45, power: 100 });
 *   unsub(); // Unsubscribe
 */

import type { GamePhase } from '../logic/gameState';
import type { CameraMode } from './cameraController';
import type { Vector2 } from './constants';

// ============================================================================
// Entity Types
// ============================================================================

export type EntityType =
  | 'projectile'
  | 'homingMissile'
  | 'mine'
  | 'sheep'
  | 'superSheep'
  | 'grave'
  | 'crate'
  | 'barrel'
  | 'fire'
  | 'napalmDroplet'
  | 'kamikaze'
  | 'raon';

export type CrateType = 'weapon' | 'health' | 'utility';

export type DestroyReason = 'exploded' | 'collected' | 'drowned' | 'timeout' | 'removed';

export type TurnEndReason = 'timeout' | 'action' | 'death' | 'skip';

export type InputDirection = 'left' | 'right' | 'up' | 'down';

export type JumpType = 'normal' | 'backflip';

// ============================================================================
// Event Data Interfaces
// ============================================================================

// Turn Events
export interface TurnStartedEvent {
  teamId: string;
  wormId: string;
  turnNumber: number;
}

export interface TurnEndedEvent {
  teamId: string;
  wormId: string;
  reason: TurnEndReason;
}

export interface TurnPhaseChangedEvent {
  from: GamePhase;
  to: GamePhase;
}

export interface TurnTimerTickEvent {
  remaining: number;
  phase: GamePhase;
}

// Weapon Events
export interface WeaponSelectedEvent {
  weaponId: string;
  teamId: string;
  wormId: string;
}

export interface WeaponFiredEvent {
  weaponId: string;
  wormId: string;
  teamId: string;
  angle: number;
  power: number;
  target?: Vector2;
  source?: 'local' | 'remote';
}

export interface WeaponChargeStartedEvent {
  weaponId: string;
  wormId: string;
}

export interface WeaponChargeReleasedEvent {
  weaponId: string;
  wormId: string;
  power: number;
}

export interface WeaponAmmoChangedEvent {
  teamId: string;
  weaponId: string;
  remaining: number;
}

export interface WeaponFuseChangedEvent {
  timer: number;
}

// Entity Events
export interface EntitySpawnedEvent {
  type: EntityType;
  id: string;
  position: Vector2;
  ownerId?: string;
  ownerTeamId?: string;
}

export interface EntityDestroyedEvent {
  type: EntityType;
  id: string;
  position: Vector2;
  reason: DestroyReason;
}

export interface EntityDamagedEvent {
  type: EntityType;
  id: string;
  damage: number;
  source: string;
}

export interface EntityMovedEvent {
  type: EntityType;
  id: string;
  position: Vector2;
  velocity: Vector2;
}

export interface EntityExplodedEvent {
  type: EntityType;
  id: string;
  position: Vector2;
  radius: number;
  maxDamage: number;
}

// Camera Events
export interface CameraModeChangedEvent {
  from: CameraMode;
  to: CameraMode;
}

export interface CameraTargetChangedEvent {
  target: Vector2 | null;
  mode: CameraMode;
}

export interface CameraTrackEntityEvent {
  type: EntityType | 'worm';
  id: string;
}

// Input Events
export interface InputMoveEvent {
  direction: InputDirection;
  pressed: boolean;
}

export interface InputJumpEvent {
  type: JumpType;
}

export interface InputFireEvent {
  pressed: boolean;
}

export interface InputWeaponSelectEvent {
  slot: number;
  weaponId?: string;
}

export interface InputTargetingEvent {
  position: Vector2;
  locked: boolean;
}

export interface InputPauseEvent {
  paused: boolean;
}

// Game Events
export interface GameStartedEvent {
  teamCount: number;
  wormCount: number;
  mapSeed: number;
}

export interface GameEndedEvent {
  winnerId: string | null;
  winnerName: string | null;
  duration: number;
}

export interface GameSuddenDeathEvent {
  waterLevel: number;
  healthReduction: number;
}

export interface GameCrateSpawnedEvent {
  crateId: string;
  crateType: CrateType;
  position: Vector2;
}

// Worm Events
export interface WormDamagedEvent {
  wormId: string;
  teamId: string;
  damage: number;
  newHealth: number;
  source: string;
}

export interface WormDiedEvent {
  wormId: string;
  teamId: string;
  position: Vector2;
  cause: 'damage' | 'drowning' | 'explosion';
}

export interface WormDamageResolvedEvent {
  wormId: string;
  teamId: string;
  damage: number;
  died: boolean;
}

// Targeting Events
export interface TargetingStartedEvent {
  weaponId: string;
  wormId: string;
}

export interface TargetingLockedEvent {
  position: Vector2;
  wormId: string;
}

export interface TargetingCancelledEvent {
  wormId: string;
}

// Tool Events
export type ToolType = 'drill' | 'blowtorch';

export interface ToolActivatedEvent {
  tool: ToolType;
}

export interface ToolDeactivatedEvent {
  tool: ToolType;
  endTurn: boolean;
}

// ============================================================================
// Event Map - Maps event names to their data types
// ============================================================================

export interface GameEventMap {
  // Turn events
  'turn:started': TurnStartedEvent;
  'turn:ended': TurnEndedEvent;
  'turn:phaseChanged': TurnPhaseChangedEvent;
  'turn:timerTick': TurnTimerTickEvent;

  // Weapon events
  'weapon:selected': WeaponSelectedEvent;
  'weapon:fired': WeaponFiredEvent;
  'weapon:chargeStarted': WeaponChargeStartedEvent;
  'weapon:chargeReleased': WeaponChargeReleasedEvent;
  'weapon:ammoChanged': WeaponAmmoChangedEvent;
  'weapon:fuseChanged': WeaponFuseChangedEvent;

  // Entity events
  'entity:spawned': EntitySpawnedEvent;
  'entity:destroyed': EntityDestroyedEvent;
  'entity:damaged': EntityDamagedEvent;
  'entity:moved': EntityMovedEvent;
  'entity:exploded': EntityExplodedEvent;

  // Camera events
  'camera:modeChanged': CameraModeChangedEvent;
  'camera:targetChanged': CameraTargetChangedEvent;
  'camera:trackEntity': CameraTrackEntityEvent;

  // Input events
  'input:move': InputMoveEvent;
  'input:jump': InputJumpEvent;
  'input:fire': InputFireEvent;
  'input:weaponSelect': InputWeaponSelectEvent;
  'input:targeting': InputTargetingEvent;
  'input:pause': InputPauseEvent;

  // Game events
  'game:started': GameStartedEvent;
  'game:ended': GameEndedEvent;
  'game:suddenDeath': GameSuddenDeathEvent;
  'game:crateSpawned': GameCrateSpawnedEvent;

  // Worm events
  'worm:damaged': WormDamagedEvent;
  'worm:died': WormDiedEvent;
  'worm:damageResolved': WormDamageResolvedEvent;

  // Targeting events
  'targeting:started': TargetingStartedEvent;
  'targeting:locked': TargetingLockedEvent;
  'targeting:cancelled': TargetingCancelledEvent;

  // Tool events
  'tool:activated': ToolActivatedEvent;
  'tool:deactivated': ToolDeactivatedEvent;
}

// ============================================================================
// Type Helpers
// ============================================================================

export type EventName = keyof GameEventMap;
export type EventData<K extends EventName> = GameEventMap[K];
export type EventHandler<K extends EventName> = (data: EventData<K>) => void;
export type Unsubscribe = () => void;

// ============================================================================
// EventBus Implementation
// ============================================================================

export class EventBus {
  private listeners = new Map<EventName, Set<EventHandler<EventName>>>();
  private onceListeners = new Map<EventName, Set<EventHandler<EventName>>>();
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebug(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  on<K extends EventName>(event: K, handler: EventHandler<K>): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as EventHandler<EventName>);

    return () => this.off(event, handler);
  }

  /**
   * Subscribe to an event, but only fire once
   */
  once<K extends EventName>(event: K, handler: EventHandler<K>): void {
    if (!this.onceListeners.has(event)) {
      this.onceListeners.set(event, new Set());
    }
    this.onceListeners.get(event)!.add(handler as EventHandler<EventName>);
  }

  /**
   * Unsubscribe from an event
   * If handler is not provided, removes all handlers for the event
   */
  off<K extends EventName>(event: K, handler?: EventHandler<K>): void {
    if (handler) {
      this.listeners.get(event)?.delete(handler as EventHandler<EventName>);
      this.onceListeners.get(event)?.delete(handler as EventHandler<EventName>);
    } else {
      this.listeners.delete(event);
      this.onceListeners.delete(event);
    }
  }

  /**
   * Emit an event to all subscribers
   */
  emit<K extends EventName>(event: K, data: EventData<K>): void {
    if (this.debugMode) {
      console.debug(`[EventBus] ${event}`, data);
    }

    // Call regular listeners
    const regularListeners = this.listeners.get(event);
    if (regularListeners) {
      for (const handler of regularListeners) {
        try {
          handler(data);
        } catch (error) {
          console.error(`[EventBus] Error in handler for '${event}':`, error);
        }
      }
    }

    // Call once listeners and remove them
    const onceListeners = this.onceListeners.get(event);
    if (onceListeners) {
      for (const handler of onceListeners) {
        try {
          handler(data);
        } catch (error) {
          console.error(`[EventBus] Error in once handler for '${event}':`, error);
        }
      }
      this.onceListeners.delete(event);
    }
  }

  /**
   * Check if an event has any listeners
   */
  hasListeners<K extends EventName>(event: K): boolean {
    const regular = this.listeners.get(event)?.size ?? 0;
    const once = this.onceListeners.get(event)?.size ?? 0;
    return regular + once > 0;
  }

  /**
   * Get count of listeners for an event
   */
  listenerCount<K extends EventName>(event: K): number {
    const regular = this.listeners.get(event)?.size ?? 0;
    const once = this.onceListeners.get(event)?.size ?? 0;
    return regular + once;
  }

  /**
   * Remove all listeners for all events
   */
  clear(): void {
    this.listeners.clear();
    this.onceListeners.clear();
  }

  /**
   * Get list of all events that have listeners
   */
  getActiveEvents(): EventName[] {
    const events = new Set<EventName>();
    for (const event of this.listeners.keys()) {
      if (this.listeners.get(event)?.size) {
        events.add(event);
      }
    }
    for (const event of this.onceListeners.keys()) {
      if (this.onceListeners.get(event)?.size) {
        events.add(event);
      }
    }
    return Array.from(events);
  }
}

// ============================================================================
// Singleton Instance (optional - can also create instances directly)
// ============================================================================

let globalEventBus: EventBus | null = null;

/**
 * Get the global EventBus instance
 * Creates one if it doesn't exist
 */
export function getEventBus(): EventBus {
  if (!globalEventBus) {
    globalEventBus = new EventBus();
  }
  return globalEventBus;
}

/**
 * Reset the global EventBus (useful for testing)
 */
export function resetEventBus(): void {
  globalEventBus?.clear();
  globalEventBus = null;
}
